/**
 * Billing, invoice, and payment types.
 */

import { BaseEntity, DateField } from './base';

// ============================================================================
// CONTRACT
// ============================================================================

export interface Contract extends BaseEntity {
  status?: ContractStatus;
  effectiveDate?: DateField;
  canceledDate?: DateField;
  terms?: string;
  caseId: string;
  createdBy: string;
  accountId: string;
  path?: string;
  parentId: string;
  percent: number;
  comments?: string;
}

export type ContractStatus = 'pending' | 'active' | 'draft' | 'completed' | 'canceled';

export interface ContractTerms {
  description: string;
  conditions?: string[];
  specialInstructions?: string;
}

// ============================================================================
// INVOICE
// ============================================================================

export interface Invoice extends BaseEntity {
  accountId: string;
  contractId: string;
  visitId: string;
  status: InvoiceStatus;
  amountDue: number;
  amountPaid: number;
  currency: string;
  stripeInvoiceId?: string;
  stripeCustomerId?: string;
  invoiceLineItemIds?: string[];
  items: InvoiceItem[];
  dueDate?: DateField;
  paidAt?: DateField;
}

export type InvoiceStatus = 'draft' | 'open' | 'paid' | 'canceled' | 'overdue';

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: "Draft",
  open: "Open",
  paid: "Paid",
  canceled: "Canceled",
  overdue: "Overdue",
};

export interface InvoiceItem extends BaseEntity {
  invoiceId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  type: InvoiceItemType;
  metadata?: Record<string, unknown>;
}

export type InvoiceItemType = 'hourly' | 'transportation' | 'miscellaneous' | 'fee';

// ============================================================================
// PAYMENT
// ============================================================================

export interface Payment extends BaseEntity {
  invoiceId: string;
  accountId: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  method: PaymentMethod;
  stripePaymentIntentId?: string;
  stripeChargeId?: string;
  metadata?: Record<string, unknown>;
}

export type PaymentStatus = 'pending' | 'succeeded' | 'failed' | 'canceled';
export type PaymentMethod = 'card' | 'bank_transfer' | 'cash' | 'check';

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  pending: "Pending",
  succeeded: "Succeeded",
  failed: "Failed",
  canceled: "Canceled",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  card: "Card",
  bank_transfer: "Bank Transfer",
  cash: "Cash",
  check: "Check",
};
